import { ReconciliationInvoice, ReconciliationSupplierOrder } from 'apis/reconciliation';
import dayjs from 'dayjs';

/** Chip state for one order row of the orders modal. When no invoice exists, `commission`
 * disambiguates *why*: null commission = terms not stamped (BE skips generation entirely);
 * stamped commission = generation is queued (async, seconds) or the order predates the
 * invoicing rollout and awaits the backfill enqueue. */
export type InvoiceChipKind = 'paid' | 'unpaid' | 'pending_terms' | 'not_generated' | 'nothing_to_bill';

/** Structural parameter (not Pick<ReconciliationSupplierOrder, …>) on purpose — the invoices
 * list feeds full ReconciliationInvoice objects through the same chip. A stamped commission of
 * exactly 0 with no invoice (waived terms, or every line cancelled) is "nothing to bill", not a
 * generation still pending — the BE voids/never creates a zero proforma. */
export const getInvoiceChipKind = (order: {
  commission: number | null;
  invoice: { isPaid: boolean } | null;
}): InvoiceChipKind => {
  if (order.invoice) return order.invoice.isPaid ? 'paid' : 'unpaid';
  if (order.commission == null) return 'pending_terms';
  return order.commission === 0 ? 'nothing_to_bill' : 'not_generated';
};

/** Same hex + rgba(…, 0.08) chip palette as bnpl-statuses.ts / STATUS_META. */
export const INVOICE_CHIP_META: Record<
  InvoiceChipKind,
  { label: string; color: string; bg: string; tooltip?: string }
> = {
  paid: { label: 'Paid', color: '#2f9e22', bg: 'rgba(47, 158, 34, 0.08)' },
  unpaid: { label: 'Unpaid', color: '#c77c00', bg: 'rgba(255, 152, 0, 0.08)' },
  pending_terms: {
    label: 'Pending terms',
    color: '#718096',
    bg: 'rgba(113, 128, 150, 0.08)',
    tooltip: 'Commission not stamped — the invoice will generate once terms are set on the GPO Orders page.',
  },
  not_generated: {
    label: 'No invoice',
    color: '#718096',
    bg: 'rgba(113, 128, 150, 0.08)',
    tooltip:
      'Invoices generate automatically shortly after an order is sent — refresh to check. Orders sent before the invoicing rollout need a backfill.',
  },
  nothing_to_bill: {
    label: 'Nothing to bill',
    color: '#718096',
    bg: 'rgba(113, 128, 150, 0.08)',
    tooltip: 'Commission is 0 for this order (waived, or every line cancelled) — no invoice is issued.',
  },
};

/** The month's live consolidation invoice — first monthly-scope entry, since the BE returns
 * `invoices` newest-first. Null when none has been generated yet. */
export const getLatestMonthlyInvoice = (invoices: ReconciliationInvoice[]): ReconciliationInvoice | null =>
  invoices.find((inv) => inv.scope === 'monthly') ?? null;

/** The monthly invoice bills the unpaid orders as of its creation — an order invoice paid *after*
 * that moment makes it stale (ops should regenerate). Best-effort by design: `paidAt` is the only
 * change signal, and unmarking clears it, which this comparator cannot see. */
export const isMonthlyInvoiceStale = (monthly: ReconciliationInvoice, invoices: ReconciliationInvoice[]): boolean =>
  invoices.some((inv) => inv.scope === 'order' && inv.paidAt !== null && dayjs(inv.paidAt).isAfter(monthly.createdAt));

/** Running totals for the orders modal's sticky header. `paidTotal`/`unpaidTotal` are Σ orderTotal
 * split by invoice paid state — orders without an invoice count in neither bucket (their payment
 * state doesn't exist yet). */
export const getOrdersTotals = (orders: ReconciliationSupplierOrder[]) => ({
  count: orders.length,
  commission: orders.reduce((sum, o) => sum + (o.commission ?? 0), 0),
  paidTotal: orders.filter((o) => o.invoice?.isPaid).reduce((sum, o) => sum + o.orderTotal, 0),
  unpaidTotal: orders.filter((o) => o.invoice && !o.invoice.isPaid).reduce((sum, o) => sum + o.orderTotal, 0),
});

/** Mirrors the BE's `MONTHLY_INVOICE_NUMBER_REGEX` (`invoice-number.ts`). Anchored, and it
 * rejects the `EK-O-` per-order series by construction — an order number must never advance
 * the monthly counter that generation draws from. Validating here as well as server-side
 * saves ops a round trip on a typo. */
export const MONTHLY_INVOICE_NUMBER_PATTERN = /^EK-\d{4}-\d{3,}$/;

export const isMonthlyInvoiceNumber = (invoiceNumber: string): boolean =>
  MONTHLY_INVOICE_NUMBER_PATTERN.test(invoiceNumber.trim());
